// src/pages/subscription/SubscriptionListPage.tsx
// Auto-generated by corebuilder. Edit freely.

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useSubscription, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';

/**
 * Subscription list page.
 */
export const SubscriptionListPage = observer(() => {
  const store = useSubscription();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');

  useEffect(() => {
    store.ListSubscriptions({
      pagination: {
        cursor: searchParams.get('cursor') || '',
        limit: 20,
        direction: 'forward',
      },
      sort: [],
      date_range: {} as any,
      filters: {} as any,
    });
  }, [searchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(search ? { search } : {});
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('subscription.list')}</h1>
        </div>
        <Link to="/subscriptions/new">
          <Button>{t('subscription.create')}</Button>
        </Link>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <Input
              placeholder={t('common.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth={false}
              className="flex-1"
            />
            <Button type="submit" variant="secondary">
              {t('common.search')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}
      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>Quantity</TableHead>
              <TableHead>Current Price</TableHead>
              <TableHead>Payment Method Id</TableHead>
              <TableHead>Latest Invoice Id</TableHead>
              <TableHead>Started At</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : store.items.length === 0 ? (
              <TableEmpty colSpan={6} message={t('subscription.noItems')} />
            ) : (
              store.items.map((item: any) => (
                <TableRow key={item.entity?.id || item.id || Math.random()}>
                  <TableCell>
                    {String(item.quantity ?? '')}
                  </TableCell>
                  <TableCell>
                    {String(item.current_price ?? '')}
                  </TableCell>
                  <TableCell>
                    {String(item.payment_method_id ?? '')}
                  </TableCell>
                  <TableCell>
                    {String(item.latest_invoice_id ?? '')}
                  </TableCell>
                  <TableCell>
                    {String(item.started_at ?? '')}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Link to={`/subscriptions/${item.entity?.id || item.id}/edit`}>
                        <Button variant="ghost" size="sm">{t('common.edit')}</Button>
                      </Link>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => {
                          if (confirm(t('subscription.deleteConfirm'))) {
                            store.DeleteSubscription(item.entity?.id || item.id);
                          }
                        }}
                      >
                        {t('common.delete')}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default SubscriptionListPage;
